// Numeric defaults referenced from multiple components/hooks. Z-index layers
// live in `./zLayers.ts` (single source of truth — import `Z_LAYERS` from
// there for stacking-order constants).

/** Estimated fixed row height used by the virtualizer when rows aren't measured. */
export const DEFAULT_ROW_HEIGHT = 52;

/** Row vertical-density presets. `"default"` maps to Normal app density (52px). */
export type RowDensity = "compact" | "default" | "comfortable";

/**
 * Single source of truth for the density → size mapping. BOTH the virtualizer
 * row-height estimate (`rowHeight`) AND the rendered cell / header vertical
 * padding (`cellPadY` / `headPadY`, applied via CSS vars on the grid root)
 * read from the same entry, so the measured slot and the painted row can
 * never drift.
 *
 * Ladder aligned with app `density.css` / `<html data-density>`:
 *   compact → 40px / py-1.5 · default (Normal) → 52px / py-3 · comfortable → 64px / py-4
 */
export const ROW_DENSITY: Record<
  RowDensity,
  { rowHeight: number; cellPadY: string; headPadY: string }
> = {
  compact: { rowHeight: 40, cellPadY: "0.375rem", headPadY: "0.5rem" },
  default: { rowHeight: DEFAULT_ROW_HEIGHT, cellPadY: "0.75rem", headPadY: "0.75rem" },
  comfortable: { rowHeight: 64, cellPadY: "1rem", headPadY: "1rem" },
};

/** Default applied when a consumer omits `rowDensity`. */
export const DEFAULT_ROW_DENSITY: RowDensity = "default";

/** CSS custom property the grid root sets and cells consume for vertical padding. */
export const ROW_DENSITY_CSS_VAR = "--dataTable-row-pad-y";

/** CSS custom property the grid root sets for header vertical padding. */
export const HEAD_DENSITY_CSS_VAR = "--dataTable-head-pad-y";

/** Extra rows rendered outside the viewport to smooth wheel/trackpad scrolls.
 *  Was 8 — at ~18 columns per row that was ~150 extra cell mounts on top of
 *  the ~30 visible, and scroll-induced mount cost dominated. 3 still hides
 *  wheel/trackpad tearing without paying for 5× more cells than needed. */
export const DEFAULT_VIRTUALIZATION_OVERSCAN = 3;

/** Indent in px per tree-row depth level for expanding rows. */
export const INDENT_PER_LEVEL = 16;

/** Debounce (ms) for per-column filter typing. */
export const FILTER_DEBOUNCE_MS = 300;

/** Debounce (ms) for load-more clicks — prevents double-fire on trackpad. */
export const LOAD_MORE_DEBOUNCE_MS = 300;

/** Default mobile breakpoint (px) below which `mobileCardRenderer` replaces the body. */
export const DEFAULT_MOBILE_BREAKPOINT = 768;

/** Default rows per page when pagination is enabled and the caller omits `pageSize`. */
export const DEFAULT_PAGE_SIZE = 50;

/**
 * Resolve the effective page size. Only a genuinely-unset (`undefined`) config
 * falls back to {@link DEFAULT_PAGE_SIZE}; any explicit value the user selected
 * in the "Items per page" control — including `25`, which is one of the offered
 * options ({@link DEFAULT_PAGE_SIZE_OPTIONS}) — is honoured verbatim. (Earlier
 * this coerced `25 → 50` to bump pre-default-change views, but that silently
 * made `25` unselectable in the drawer, so the value is now respected.)
 */
export const resolveTablePageSize = (pageSize?: number): number => pageSize ?? DEFAULT_PAGE_SIZE;

/** Default page-size options shown in the pagination footer selector. */
export const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
